import * as React from "react"
import { gql } from "@apollo/client"
import {
  Box,
  Button,
  Center,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import Head from "next/head"
import "dayjs/locale/nl"

dayjs.extend(utc)
dayjs.locale("nl")

import { paginate } from "lib/apollo/helpers"
import { EventItemFragment, QueryMode, SortOrder, useGetEventsQuery } from "lib/graphql"
import { useLocalStorage } from "lib/hooks/useLocalStorage"
import { EventFilters, EventsFilterbar } from "components/EventsFilterbar"
import { EventTile } from "components/EventTile"
import { Layout } from "components/Layout"
import { Limiter } from "components/Limiter"
import { Search } from "components/Search"

const _ = gql`
  query GetEvents(
    $take: Int
    $orderBy: [EventOrderByWithRelationInput!]
    $where: EventWhereInput
    $skip: Int
  ) {
    events(take: $take, orderBy: $orderBy, where: $where, skip: $skip) {
      items {
        ...EventItem
      }
      count
    }
  }
`

const TAKE = 40 // Note: Bumping up the TAKE value to decrease the likelihood that we end up showing one or two month's events when actually there are a load more that could be loaded.
export default function Events() {
  const colorPlaceholder = useColorModeValue("grey", "white")
  const [search, setSearch] = React.useState("")
  const [filters, setFilters] = useLocalStorage<EventFilters>("kunstbende.filters.events", {
    type: "",
    region: "",
    category: "",
  })

  const { data, loading, fetchMore } = useGetEventsQuery({
    variables: {
      orderBy: [{ date: SortOrder.Asc }, { name: SortOrder.Asc }],
      take: TAKE,
      where: {
        isPublished: { equals: true },
        date: { gte: dayjs.utc().format("YYYY-MM-DD") },
        type: { equals: filters.type || undefined },
        region: { equals: filters.region || undefined },
        categories: filters.category ? { has: filters.category } : undefined,
        OR: [
          { name: { contains: search, mode: QueryMode.Insensitive } },
          { location: { contains: search, mode: QueryMode.Insensitive } },
        ],
      },
    },
  })

  const handleFetchMore = () => {
    if (!events) return
    return fetchMore({
      variables: {
        skip: events.length,
        take: TAKE,
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updateQuery: paginate("events"),
    })
  }

  const events = data?.events.items

  const eventCount = data?.events.count

  const groupedEvents = React.useMemo(
    () =>
      events?.reduce((acc, event) => {
        const date = dayjs.utc(event.date).format("MMMM - YYYY")
        if (acc[date]) {
          acc[date].push(event)
        } else {
          acc[date] = [event]
        }
        return acc
      }, {} as { [date: string]: EventItemFragment[] }),
    [events],
  )
  return (
    <>
      <Head>
        <title>Kunstbende - Agenda</title>
      </Head>

      <Limiter>
        <Stack w="100%" mb={{ base: 40, lg: 40 }} spacing={{ base: 4, md: 4 }} mt={16}>
          <Text
            fontSize={{ base: 32, md: 56 }}
            fontWeight={900}
            lineHeight={{ base: "shorter", md: "short" }}
            px={0}
            textAlign="left"
            role="heading"
            aria-level={1}
          >
            Agenda
          </Text>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <Search
              w="100%"
              search={search}
              onSearch={setSearch}
              placeholder="Zoeken"
              _placeholder={{ color: colorPlaceholder }}
            />
            <EventsFilterbar filters={filters} onSetFilters={setFilters} />
          </SimpleGrid>

          {loading ? (
            <Center h={300}>
              <Spinner />
            </Center>
          ) : !!!events ? (
            <Center h={300}>
              <Text fontWeight="bold" fontSize="2xl">
                Fout bij ophalen agenda
              </Text>
            </Center>
          ) : events.length === 0 ? (
            <Center h={300}>
              <Text fontWeight="bold" fontSize="2xl">
                Geen agenda gevonden
              </Text>
            </Center>
          ) : (
            <Box role="main" aria-label="Agenda overzicht">
              {groupedEvents &&
                Object.entries(groupedEvents).map(([date, events], i) => {
                  return (
                    <Box key={date}>
                      <Flex
                        m={0}
                        mt={{ base: 6, sm: i === 0 ? 6 : 24 }}
                        mb={{ base: 2, sm: 6 }}
                        borderBottom="4px solid"
                        borderColor="gray.400"
                        w="100%"
                        justify="space-between"
                        align="flex-end"
                      >
                        <Text
                          fontWeight="black"
                          textTransform="uppercase"
                          fontSize={{ base: "2xl", md: "3xl" }}
                          role="heading"
                          aria-level={2}
                        >
                          {date}
                        </Text>
                        {/* <Text fontSize="sm" align="center">{`${events.length} agenda${
                          events.length > 1 ? "s" : ""
                        }`}</Text> */}
                      </Flex>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                        {events.map((event) => (
                          <EventTile key={event.id} event={event} />
                        ))}
                      </SimpleGrid>
                    </Box>
                  )
                })}
              {eventCount && events.length < eventCount ? (
                <Center>
                  <Button onClick={handleFetchMore} colorScheme="yellow">
                    Meer laden
                  </Button>
                </Center>
              ) : null}
            </Box>
          )}
        </Stack>
      </Limiter>
    </>
  )
}

Events.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>
